// path: src/Components/row/Row.js

import React, { useEffect, useRef, useState } from "react";
import Card from "@material-ui/core/Card";
import "./row.css";
import BoostIcon from "../boosts/BoostIcon";
import { getBoostCatalogEntryByItemId } from "../../utils/boosts";
import { getCardImageSrc } from "../../utils/cardImages";
import {
    isHiddenBoostKind,
    isHiddenPackKind,
} from "../../utils/hiddenBoardLoot";

const SHY_CORGIS_START_DELAY_MS = 5000;
const SHY_CORGIS_MIN_MS = 3000;
const SHY_CORGIS_MAX_MS = 7000;
const SHY_CORGIS_RETRY_AFTER_DENIED_MS = 900;

const getRandomShyCorgisDelay = () => {
    return (
        SHY_CORGIS_MIN_MS +
        Math.floor(Math.random() * (SHY_CORGIS_MAX_MS - SHY_CORGIS_MIN_MS + 1))
    );
};

const Row = (card) => {
    const delayMs = (card.index ?? 0) * 70;
    const [animateIn, setAnimateIn] = useState(true);
    const [tapFeedback, setTapFeedback] = useState(false);
    const [shyHidden, setShyHidden] = useState(false);

    const lastLevelKeyRef = useRef(card.levelKey);
    const tapFeedbackTimerRef = useRef(null);
    const shyHideTimerRef = useRef(null);
    const shyShowTimerRef = useRef(null);
    const shyStartTimerRef = useRef(null);

    const clearShyCorgisTimers = () => {
        if (shyStartTimerRef.current) {
            clearTimeout(shyStartTimerRef.current);
            shyStartTimerRef.current = null;
        }

        if (shyHideTimerRef.current) {
            clearTimeout(shyHideTimerRef.current);
            shyHideTimerRef.current = null;
        }

        if (shyShowTimerRef.current) {
            clearTimeout(shyShowTimerRef.current);
            shyShowTimerRef.current = null;
        }
    };

    const releaseShyHide = () => {
        if (card.releaseShyCorgiHide) {
            card.releaseShyCorgiHide(card.id);
        }
    };

    useEffect(() => {
        if (lastLevelKeyRef.current !== card.levelKey) {
            lastLevelKeyRef.current = card.levelKey;
            setAnimateIn(true);
        }

        const t = setTimeout(() => setAnimateIn(false), 900);
        return () => clearTimeout(t);
    }, [card.levelKey]);

    useEffect(() => {
        return () => {
            if (tapFeedbackTimerRef.current) {
                clearTimeout(tapFeedbackTimerRef.current);
            }

            clearShyCorgisTimers();
            releaseShyHide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        clearShyCorgisTimers();
        setShyHidden(false);
        releaseShyHide();

        if (!card.shyCorgisMode) {
            return undefined;
        }

        let cancelled = false;

        const scheduleNextCycle = () => {
            if (cancelled) {
                return;
            }

            const delay = getRandomShyCorgisDelay();

            shyHideTimerRef.current = setTimeout(() => {
                if (cancelled) {
                    return;
                }

                const allowedToHide = card.requestShyCorgiHide
                    ? card.requestShyCorgiHide(card.id)
                    : true;

                if (!allowedToHide) {
                    shyHideTimerRef.current = setTimeout(() => {
                        scheduleNextCycle();
                    }, SHY_CORGIS_RETRY_AFTER_DENIED_MS);
                    return;
                }

                setShyHidden(true);

                shyShowTimerRef.current = setTimeout(() => {
                    if (cancelled) {
                        return;
                    }

                    setShyHidden(false);
                    releaseShyHide();
                    scheduleNextCycle();
                }, delay);
            }, delay);
        };

        const startDelayMs =
            Number.isFinite(Number(card.shyStartDelayMs)) &&
            Number(card.shyStartDelayMs) >= 0
                ? Number(card.shyStartDelayMs)
                : SHY_CORGIS_START_DELAY_MS;

        shyStartTimerRef.current = setTimeout(() => {
            scheduleNextCycle();
        }, startDelayMs);

        return () => {
            cancelled = true;
            clearShyCorgisTimers();
            setShyHidden(false);
            releaseShyHide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [card.shyCorgisMode, card.id, card.levelKey, card.shyStartDelayMs]);

    const triggerTapFeedback = () => {
        setTapFeedback(true);

        if (tapFeedbackTimerRef.current) {
            clearTimeout(tapFeedbackTimerRef.current);
        }

        tapFeedbackTimerRef.current = setTimeout(() => {
            setTapFeedback(false);
        }, 140);

        if (window.navigator && typeof window.navigator.vibrate === "function") {
            window.navigator.vibrate(18);
        }
    };

    const handleCardClick = (event) => {
        triggerTapFeedback();

        if (card.handleClick) {
            card.handleClick(event);
        }
    };

    const faceHidden = shyHidden && !card.guideHighlighted;
    const isBoostReveal = Boolean(card.boostRevealHighlighted);
    const hiddenLootKind = card.hiddenLootKind || null;
    const hiddenBoostEntry = isHiddenBoostKind(hiddenLootKind)
        ? getBoostCatalogEntryByItemId(hiddenLootKind)
        : null;
    const hiddenLootClass = hiddenLootKind
        ? isHiddenPackKind(hiddenLootKind)
            ? "card--hiddenPack"
            : "card--hiddenBoost"
        : "";

    return (
        <Card
            raised={true}
            className={`card ${animateIn ? "card--cinematicEnter" : ""} ${
                card.isFoil ? "card--foil" : ""
            } ${hiddenLootClass} ${tapFeedback ? "card--tapFeedback" : ""} ${
                faceHidden ? "card--shyHidden" : ""
            } ${card.guideHighlighted ? "card--guideHighlight" : ""} ${
                isBoostReveal ? "card--boostReveal" : ""
            } ${card.guideDimmed ? "card--guideDimmed" : ""} ${
                card.anxiousWarning ? "card--anxiousWarn" : ""
            }`}
            style={{
                ...(animateIn ? { animationDelay: `${delayMs}ms` } : {}),
                ...(card.anxiousWarning
                    ? {
                          "--anxious-delay": `${(card.index ?? 0) * 45}ms`,
                      }
                    : {}),
            }}
        >
            <div className="cardFlip">
                <div
                    className="cardFlipInner"
                    style={{
                        transform: `rotateY(${faceHidden ? 180 : 0}deg)`,
                    }}
                >
                    <button
                        type="button"
                        className="cardFlipBack"
                        aria-label={`${card.name} is hiding`}
                        onClick={handleCardClick}
                        id={card.id}
                        data-foil={card.isFoil ? "true" : "false"}
                    />

                    <img
                        src={getCardImageSrc(card)}
                        alt={card.name}
                        className="card-image cardFlipFront"
                        onClick={handleCardClick}
                        id={card.id}
                        data-foil={card.isFoil ? "true" : "false"}
                        style={{
                            transform: `rotate(${card.twistedRotation || 0}deg)`,
                        }}
                    />

                    {card.isFoil && !faceHidden && (
                        <span
                            className="gameFoilIcon"
                            title="Foil card"
                            role="img"
                            aria-label="Foil card"
                        >
                            ✨
                        </span>
                    )}

                    {hiddenLootKind && !faceHidden && (
                        <span
                            className={`gameHiddenLootIcon${
                                isHiddenPackKind(hiddenLootKind)
                                    ? " gameHiddenLootIcon--pack"
                                    : " gameHiddenLootIcon--boost"
                            }`}
                            title={
                                isHiddenPackKind(hiddenLootKind)
                                    ? "Hidden pack"
                                    : "Hidden boost"
                            }
                            role="img"
                            aria-label={
                                isHiddenPackKind(hiddenLootKind)
                                    ? "Hidden pack"
                                    : "Hidden boost"
                            }
                        >
                            {isHiddenPackKind(hiddenLootKind) ? (
                                "📦"
                            ) : (
                                <BoostIcon
                                    boost={hiddenBoostEntry}
                                    size={14}
                                />
                            )}
                        </span>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default Row;